// Shared Framer Motion variants (consumed across the SPA — Agent D builds the
// page visuals against these; this agent owns the library).
//
// Motion language (WEBSITE-PLAN §5.3): calm, confident, "setting sail" momentum —
// never bouncy or noisy. Durations 300–500ms, ease-out.
//
// Accessibility: every variant honors `prefers-reduced-motion`. When the user
// asks for less, motion collapses to instant (no translate, ~0s) so reveals still
// flip to their visible state but without movement. `prefersReducedMotion()` is
// read at module load (variants are static objects). It also updates on the rare
// case the OS setting flips mid-session if a consumer re-reads it; consumers that
// need live reactivity should use the `useReducedMotion()` hook from framer-motion.
import type { Variants, Transition } from "framer-motion";

/** True when the user has requested reduced motion (read at module evaluation). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Back-compat alias used by the Phase 0 scaffold. */
export const reducedMotion = prefersReducedMotion();

/** Calm ease-out curve ("setting sail"); 0.4s, collapsed when reduced. */
const baseTransition: Transition = {
  duration: reducedMotion ? 0 : 0.4,
  ease: [0.16, 1, 0.3, 1],
};

// ---------------------------------------------------------------------------
// fadeUp — the workhorse scroll-reveal.
//   Usage: <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
//           viewport={revealViewport}> … </motion.div>
//   As a stagger child, omit initial/whileInView and let the parent drive it.
// ---------------------------------------------------------------------------
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: reducedMotion ? 0 : 16 },
  visible: { opacity: 1, y: 0, transition: baseTransition },
};

// ---------------------------------------------------------------------------
// staggerContainer — parent that staggers its children's entrance.
//   Usage: <motion.ul variants={staggerContainer} initial="hidden"
//           whileInView="visible" viewport={revealViewport}>
//             {items.map(i => <motion.li variants={fadeUp} key={i}/>)}
//          </motion.ul>
// ---------------------------------------------------------------------------
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: reducedMotion ? 0 : 0.08,
      delayChildren: reducedMotion ? 0 : 0.05,
    },
  },
};

/** Back-compat alias (Phase 0 scaffold named it `stagger`). */
export const stagger = staggerContainer;

// ---------------------------------------------------------------------------
// pageTransition — subtle cross-fade + 8px slide on route change.
//   Usage (Layout wraps the <Outlet/>): <motion.div variants={pageTransition}
//           initial="initial" animate="enter" exit="exit"> … </motion.div>
//   inside an <AnimatePresence mode="wait">.
// ---------------------------------------------------------------------------
export const pageTransition: Variants = {
  initial: { opacity: 0, y: reducedMotion ? 0 : 8 },
  enter: { opacity: 1, y: 0, transition: baseTransition },
  exit: {
    opacity: 0,
    y: reducedMotion ? 0 : -8,
    transition: { ...baseTransition, duration: reducedMotion ? 0 : 0.3 },
  },
};

// ---------------------------------------------------------------------------
// drawLine — for SVG route arcs (Agent D's animated world map). Animates
// stroke-dashoffset from 1→0 to "draw" a path. The element must set
// pathLength={1} and a dash array, e.g.:
//   <motion.path pathLength={1} strokeDasharray="1" variants={drawLine}
//     initial="hidden" whileInView="visible" viewport={revealViewport}/>
// Reduced motion: the path appears fully drawn instantly.
// ---------------------------------------------------------------------------
export const drawLine: Variants = {
  hidden: { strokeDashoffset: reducedMotion ? 0 : 1, opacity: reducedMotion ? 1 : 0 },
  visible: {
    strokeDashoffset: 0,
    opacity: 1,
    transition: {
      strokeDashoffset: { duration: reducedMotion ? 0 : 1.2, ease: [0.16, 1, 0.3, 1] },
      opacity: { duration: reducedMotion ? 0 : 0.2 },
    },
  },
};

/** Common viewport config for one-shot scroll reveals (fire once, 20% in view). */
export const revealViewport = { once: true, amount: 0.2 } as const;
